import { faHeartbeat } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation, faMoon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { status } from '@spike/appointment-model';
import clsx from 'clsx';
import { AppointmentEventContentArg } from 'components/Calendar3/Boarding/WeekFullCalendar';
import { Moment } from 'moment-timezone';
import React from 'react';
import styles from './EventCell.module.scss';

type EventCellProps = AppointmentEventContentArg & {
    timeZone: string;
};

type CheckMoment = {
    date: string;
    time: string;
};

const formatCheckMoment = (dateTime: unknown, timeZone: string): CheckMoment | undefined => {
    if (!dateTime || typeof (dateTime as { clone?: unknown }).clone !== 'function') return undefined;

    const localizedDateTime = (dateTime as Moment).clone().tz(timeZone);
    if (!localizedDateTime.isValid()) return undefined;

    return {
        date: localizedDateTime.format('MMM, D'),
        time: localizedDateTime.format('h:mma')
    };
};

export const EventCell: React.FC<EventCellProps> = ({ event, timeZone }) => {
    const checkIn = formatCheckMoment(event.extendedProps.appointment.startAt, timeZone);
    const checkOut = formatCheckMoment(event.extendedProps.appointment.endAt, timeZone);

    return (
        <div
            className={clsx(
                styles.root,
                {
                    [styles.booked]: event.extendedProps.appointment.status.id === status.BOOKED
                },
                {
                    [styles.confirmed]: event.extendedProps.appointment.status.id === status.CONFIRMED
                },
                {
                    [styles.checkedIn]: event.extendedProps.appointment.status.id === status.CHECKED_IN
                },
                {
                    [styles.inProgress]: event.extendedProps.appointment.status.id === status.IN_PROGRESS
                },
                {
                    [styles.readyForCheckout]: event.extendedProps.appointment.status.id === status.READY_FOR_CHECK_OUT
                },
                {
                    [styles.payment]: event.extendedProps.appointment.status.id === status.PAYMENT
                },
                {
                    [styles.completed]: event.extendedProps.appointment.status.id === status.COMPLETED
                },
                {
                    [styles.requested]: event.extendedProps.appointment.status.id === status.REQUESTED
                }
            )}
            style={{ '--service-color': event.extendedProps.appointment.service.color } as React.CSSProperties}
        >
            <div className={styles.details}>
                <div className={styles.petInfo}>
                    <p className={styles.title}>
                        {event.extendedProps.appointment.pet.name}
                        {event.extendedProps.appointment.pet.hasMedicalConditions && (
                            <FontAwesomeIcon
                                icon={faHeartbeat}
                                className={styles.medicalConditionsIcon}
                            />
                        )}
                        {event.extendedProps.appointment.pet.hasNegativeTags && (
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className={styles.negativePersonalitiesIcon}
                            />
                        )}
                    </p>

                    <p>{event.extendedProps.appointment.pet.breed?.name}</p>

                    <p>
                        <FontAwesomeIcon icon={faMoon} />
                        <span>
                            {event.extendedProps.appointment.nights} Night
                            {event.extendedProps.appointment.nights > 1 ? 's' : ''}
                        </span>
                    </p>
                </div>

                {checkIn && (
                    <div className={styles.checkMoment}>
                        <span className={styles.checkLabel}>Check-In</span>
                        <span className={styles.checkValue}>{checkIn.date}</span>
                        <span className={styles.checkValue}>{checkIn.time}</span>
                    </div>
                )}

                {checkOut && (
                    <div className={styles.checkMoment}>
                        <span className={styles.checkLabel}>Check-Out</span>
                        <span className={styles.checkValue}>{checkOut.date}</span>
                        <span className={styles.checkValue}>{checkOut.time}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
